import SuperFetch from 'library/helpers/superFetch';
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { Notification } from 'zComponents/atoms';
import { hasInviteChannel } from './helpers';
import { visitsActions } from './slice';

const BASE = '/visits';
/**
 * 🛑 A DIFFERENT MOUNT, not a path under /visits. The kiosk ADMIN surface is
 * `/v5/:orgID/visitor-kiosks` (routesV5/index.js:385) and the tablet's own boundary is the
 * org-less `/v5/visitor-kiosk` — singular, pre-auth, and nothing here ever calls it. The two names
 * differ by one character and have different auth, so keep them apart by constant rather than by
 * interpolation.
 */
const KIOSKS = '/visitor-kiosks';

const errMessage = (res, fallback) => res.error?.message || res.error || res.message || fallback;

export function* loadTypes() {
	try {
		const { orgID } = yield select((state) => state.Auth.selectedProfile);
		const res = yield call(() => SuperFetch.get(`${BASE}/types`, {}, { includeInactive: true }));
		if (res.statusCode !== 200) throw Error(errMessage(res, 'Failed to load visitor types.'));
		yield put(visitsActions.loadTypesSuccess({ orgID, types: res.data || [] }));
	} catch (error) {
		Notification('error', error.message);
		yield put(visitsActions.loadTypesError(error));
	}
}

/**
 * One saga for create / update / delete.
 *
 * 🛑 IT REFETCHES RATHER THAN PATCHING THE CACHE. The server owns three things the client cannot
 * reproduce: it applies Joi defaults to every step (`required: false`, `requires: 'either'`), it
 * assigns the id on create, and it stamps ModifiedDate. Splicing the request body into the list
 * would show the operator their INPUT and call it the saved state — so a default the server filled
 * in, or a field it rejected and dropped, would not appear until a reload.
 *
 * The list is small (a handful of types per org) so the refetch is cheap, and being able to trust
 * the grid is worth more than one round trip.
 */
export function* mutateType({ payload }) {
	const { method, path, body, successMessage } = payload;
	try {
		const call$ =
			method === 'DELETE'
				? () => SuperFetch.delete(`${BASE}${path}`)
				: method === 'PATCH'
					? () => SuperFetch.patch(`${BASE}${path}`, body, {})
					: () => SuperFetch.post(`${BASE}${path}`, body);
		const res = yield call(call$);
		if (res.statusCode !== 200 && res.statusCode !== 201) throw Error(errMessage(res, 'Save failed.'));
		Notification('success', successMessage || 'Saved.');
		// A CREATE hands back the saved type, so the editor can stay open on it: a document step can
		// only be added to a type that already exists (its PDF is keyed on the type's id).
		yield put(visitsActions.mutateTypeSuccess(method === 'POST' ? (res.data ?? null) : null));
		yield call(loadTypes);
	} catch (error) {
		Notification('error', error.message);
		yield put(visitsActions.mutateTypeError(error));
	}
}

/**
 * Read one level's site config. `siteID` null/undefined asks for the ORG default.
 *
 * ⚠️ The API returns { orgRow, siteRow, resolved } and all three are stored. `resolved` is what the
 * tablet will render; the raw rows are how the editor tells inherited from overridden.
 */
export function* loadSiteConfig({ payload }) {
	const siteID = payload?.siteID ?? null;
	try {
		const { orgID } = yield select((state) => state.Auth.selectedProfile);
		const res = yield call(() => SuperFetch.get(`${BASE}/site-config`, {}, siteID == null ? {} : { siteID }));
		if (res.statusCode !== 200) throw Error(errMessage(res, 'Failed to load lobby settings.'));
		yield put(
			visitsActions.loadSiteConfigSuccess({ orgID, levelKey: siteID == null ? 'org' : siteID, config: res.data })
		);
	} catch (error) {
		Notification('error', error.message);
		yield put(visitsActions.loadSiteConfigError(error));
	}
}

/**
 * Save one level's config, then refetch it — same reasoning as mutateType: the server owns Joi
 * defaults and ModifiedDate, and echoing the request body back would show the operator their INPUT
 * and call it saved state.
 *
 * 🛑 `siteID` IS SENT IN THE BODY, ALWAYS, INCLUDING `null`. The server's upsert requires it
 * explicitly so that omitting it cannot silently mean "write the org default" — and `SuperFetch.put`
 * would drop an undefined key, which is exactly the accident the API is shaped to prevent.
 */
export function* saveSiteConfig({ payload }) {
	const { siteID = null, branding, behaviour } = payload || {};
	try {
		const body = { siteID };
		// Only the half being edited is sent; the server COALESCEs, so omitting `behaviour` keeps
		// the stored one rather than clearing it. Saving a logo must not reset the sign-out time.
		if (branding !== undefined) body.branding = branding;
		if (behaviour !== undefined) body.behaviour = behaviour;

		const res = yield call(() => SuperFetch.put(`${BASE}/site-config`, body, {}));
		if (res.statusCode !== 200) throw Error(errMessage(res, 'Save failed.'));
		Notification('success', 'Lobby settings saved.');
		yield put(visitsActions.saveSiteConfigSuccess());
		yield call(loadSiteConfig, { payload: { siteID } });
	} catch (error) {
		Notification('error', error.message);
		yield put(visitsActions.saveSiteConfigError(error));
	}
}

/* ── Lobby tablets (kiosks) ─────────────────────────────────────────────────────────────────── */

export function* loadKiosks() {
	try {
		const { orgID } = yield select((state) => state.Auth.selectedProfile);
		const res = yield call(() => SuperFetch.get(KIOSKS));
		if (res.statusCode !== 200) throw Error(errMessage(res, 'Failed to load lobby tablets.'));
		yield put(visitsActions.loadKiosksSuccess({ orgID, kiosks: res.data || [] }));
	} catch (error) {
		Notification('error', error.message);
		yield put(visitsActions.loadKiosksError(error));
	}
}

/**
 * When the current code-minting write started, or 0 for none.
 *
 * 🛑 SAGA-LOCAL, NOT `state.Visits.mutationLoading`, AND THE REASON IS ORDERING. The reducer for
 * `mutateKiosk` runs BEFORE this saga does, so by the time the saga can read the store the flag is
 * already true for the very action it is checking — every create and re-pair would refuse itself.
 *
 * 🛑 A TIMESTAMP, NOT A BOOLEAN, BECAUSE A BOOLEAN CAN WEDGE. `finally` covers a throw, an early
 * return and saga cancellation — but NOT a `yield call` that never settles, and SuperFetch sets no
 * timeout and no AbortController. One stalled POST (captive-portal Wi-Fi, a proxy holding the
 * socket) would leave a boolean true for the life of the tab: every later create and re-pair
 * refused, recoverable only by a page reload. An expiring marker degrades instead — it stops
 * protecting after the window, which is the right direction when the alternative is a dead screen.
 */
let kioskWriteStartedAt = 0;

/** Long enough to cover a slow write, short enough that a stall self-heals. */
const WRITE_WINDOW_MS = 30_000;

/**
 * Test-only: drop the in-flight marker.
 *
 * ⚠️ Exported ONLY because the marker is module-level and a test that abandons a generator
 * mid-flight leaves it stamped — which then fails the NEXT test for a reason unrelated to what it
 * asserts. Production code must never call this: releasing a marker you do not own is the very
 * defect the generation compare exists to prevent.
 */
export const releaseKioskWriteMarkerForTests = () => {
	kioskWriteStartedAt = 0;
};

/**
 * Create / re-pair / revoke, then refetch the list.
 *
 * 🛑 THE PAIRING CODE IS TAKEN OUT OF THE RESPONSE AND PUT IN THE STORE BEFORE THE REFETCH.
 * It is returned exactly once — the server keeps only its HMAC — so if the refetch failed and this
 * saga bailed first, the only copy of a credential the operator has already been told about would
 * be gone, and recovering it means re-pairing, which for a live tablet kills its credential.
 *
 * ⚠️ A failed REFETCH is therefore not a failed WRITE. The write already happened; the list is
 * merely stale. Reporting it as a failure would invite the operator to press the button again and
 * mint a second kiosk.
 */
export function* mutateKiosk({ payload }) {
	const { path = '', body, successMessage } = payload || {};

	/**
	 * 🛑 ONE UNDISPLAYED CODE AT A TIME. `mutateKiosk` is takeEvery, the table is not masked while a
	 * write runs, and `Modal.confirm` closes on OK — so an operator can confirm Re-pair on a second
	 * row while the first POST is still in flight. Both old credentials are dead server-side by
	 * then, and the second response overwrites the first code in the store before anyone read it:
	 * that tablet is out of service until someone re-pairs it a third time, with nothing saying why.
	 *
	 * Two conditions, because they cover different windows. `kioskWriteStartedAt` is the request
	 * window; `pairingIssue` is the window after the code lands and before it is dismissed.
	 *
	 * 🛑 REVOKE IS EXEMPT. It mints no code, so it cannot overwrite one — and it is the security
	 * action. Refusing it because a code is on screen somewhere would mean a lost or stolen tablet
	 * cannot be cut off until someone navigates back and dismisses a dialog.
	 */
	const isRevoke = path.endsWith('/revoke');
	const pendingCode = yield select((state) => state.Visits?.pairingIssue);
	// ⚠️ `Math.abs`, because a NEGATIVE delta is not 'in flight'. An NTP step-back or a VM resume
	// that rewinds the wall clock leaves `Date.now() - kioskWriteStartedAt` negative, which a plain
	// `< WINDOW` reads as in-flight — refusing every create and re-pair for as long as the clock is
	// behind, recoverable only by a reload. That is the exact wedge this was made a timestamp to
	// avoid, arriving from the other direction.
	const age = Math.abs(Date.now() - kioskWriteStartedAt);
	const writeInFlight = kioskWriteStartedAt > 0 && age < WRITE_WINDOW_MS;
	if (!isRevoke && (writeInFlight || pendingCode)) {
		Notification(
			'warning',
			pendingCode
				? 'Finish with the pairing code on screen first.'
				: 'One tablet at a time — that one is still saving.'
		);
		// 🛑 THE REDUCER ALREADY RAN. It set mutationLoading true before this saga was reached, so a
		// bare `return` leaves the Add button spinning for ever — a refusal that looks like a hang.
		// Reporting it as a failed write is also honest: nothing was written.
		yield put(visitsActions.mutateKioskError({ message: 'Another kiosk change is still in progress.' }));
		return;
	}

	// 🛑 REVOKE NEITHER CHECKS THE MARKER NOR SETS IT. Setting it would be harmless; CLEARING it in
	// the `finally` is not — a quick revoke finishing while a slower re-pair is still in flight
	// would reopen the window for a second code-minting write, which is the exact overlap the
	// marker exists to prevent. The guard would then depend on the UI it is defending against.
	//
	// 🛑 AND THE STAMP IS CAPTURED SO THE RELEASE CAN CHECK IT IS STILL OURS. `mutateKiosk` is
	// takeEvery, so once the window lapses two writes CAN overlap: #1 stalls past 30s, the operator
	// clicks again, #2 starts and re-stamps — and then #1 finally settles and its `finally` wipes
	// #2's marker, leaving a third write completely unguarded. That is the same defect as the
	// revoke-clearing one above, one level down: releasing a lock you no longer hold.
	const startedAt = Date.now();
	if (!isRevoke) kioskWriteStartedAt = startedAt;
	try {
		const res = yield call(() => SuperFetch.post(`${KIOSKS}${path}`, body || {}));
		if (res.statusCode !== 200 && res.statusCode !== 201) throw Error(errMessage(res, 'Failed.'));
		// Present for create and re-pair, absent for revoke.
		const issue = res.data?.pairingCode
			? {
					kioskDeviceID: res.data.kioskDeviceID,
					pairingCode: res.data.pairingCode,
					pairingExpiresAt: res.data.pairingExpiresAt
				}
			: null;
		yield put(visitsActions.mutateKioskSuccess({ issue }));
		// 🛑 ALWAYS notify. Create and re-pair are self-evident — a dialog with a code appears — but
		// REVOKE has no dialog, so with a conditional message its only feedback was the row changing
		// after the refetch. A slow or failed refetch (which this saga deliberately does not report as
		// a failed write) left the operator looking at an unchanged table after confirming a
		// destructive action, and clicking it again.
		Notification('success', successMessage || 'Done.');
	} catch (error) {
		Notification('error', error.message);
		yield put(visitsActions.mutateKioskError(error));
		return;
	} finally {
		// 🛑 `finally`, so an early return or a throw cannot leave the marker set and lock every
		// subsequent write out of the screen. It does NOT cover a request that never settles —
		// that is what the expiry window above is for.
		//
		// ⚠️ AND ONLY IF THE MARKER IS STILL OURS. An unconditional clear releases a lock a LATER
		// write is holding — see the stamp above.
		if (!isRevoke && kioskWriteStartedAt === startedAt) kioskWriteStartedAt = 0;
	}
	yield call(loadKiosks);
}

export function* loadVisits({ payload }) {
	try {
		const { orgID } = yield select((state) => state.Auth.selectedProfile);
		const { from, to, siteID, status, pageSize } = payload || {};
		const query = {};
		if (from) query.from = from;
		if (to) query.to = to;
		if (siteID != null) query.siteID = siteID;
		if (status) query.status = status;
		if (pageSize) query.pageSize = pageSize;
		// 🛑 On-site is a live condition, not a date. A visit checked in before this window is still
		// on site, and the summary tiles (server stats) count it — so a window-only read showed
		// "On site now 6" over an empty On-site list, with no way to check those six out. Every open
		// check-in is fetched alongside the window (no from/to = no date bound) and merged by id.
		const openCheckIns =
			from && !status
				? { status: 'checked_in', ...(siteID != null ? { siteID } : {}), ...(pageSize ? { pageSize } : {}) }
				: null;
		const [res, onSiteRes] = yield all([
			call(() => SuperFetch.get(BASE, {}, query)),
			openCheckIns ? call(() => SuperFetch.get(BASE, {}, openCheckIns)) : call(() => null)
		]);
		if (res.statusCode !== 200) throw Error(errMessage(res, 'Failed to load visits.'));
		const windowRecords = res.data || [];
		const inWindow = new Set(windowRecords.map((r) => r.visitID));
		// A failed open-check-in read degrades to the window alone; it never fails the list.
		const onSiteExtra =
			onSiteRes?.statusCode === 200 ? (onSiteRes.data || []).filter((r) => !inWindow.has(r.visitID)) : [];
		const records = [...windowRecords, ...onSiteExtra];
		// The list is one page of at most `pageSize`. Say so when the server's total is larger, rather
		// than letting the grid present a cut-off window as the whole of it. Judged on the WINDOW read.
		const total = Number(res.pagination?.total);
		const truncated = Number.isFinite(total)
			? total > windowRecords.length
			: Boolean(pageSize && windowRecords.length >= pageSize);
		yield put(visitsActions.loadVisitsSuccess({ orgID, records, truncated }));
	} catch (error) {
		Notification('error', error.message);
		yield put(visitsActions.loadVisitsError(error));
	}
}

/**
 * Server-computed stats for the visits summary (GET /visits/stats).
 *
 * 🛑 FAILURE IS SILENT ON PURPOSE — no Notification, no `state.error`. The summary falls back to
 * client-derived counts from the already-loaded records, so while the endpoint is not deployed on
 * an environment this page must not toast an error on every load for a page that works.
 */
export function* loadVisitStats({ payload }) {
	try {
		const { orgID } = yield select((state) => state.Auth.selectedProfile);
		const { from, to, siteID } = payload || {};
		const query = {};
		if (from) query.from = from;
		if (to) query.to = to;
		if (siteID != null) query.siteID = siteID;
		const res = yield call(() => SuperFetch.get(`${BASE}/stats`, {}, query));
		if (res.statusCode !== 200) throw Error(errMessage(res, 'Stats unavailable.'));
		yield put(visitsActions.loadVisitStatsSuccess({ orgID, stats: res.data || null }));
	} catch (error) {
		yield put(visitsActions.loadVisitStatsError());
	}
}

/**
 * The transition routes a lifecycle action may target. An allowlist, not interpolation of caller
 * input into the URL: `action` comes from component code today, but a constant table means a typo'd
 * or novel action fails loudly here rather than becoming a request to an unintended path.
 */
const VISIT_ACTIONS = Object.freeze({
	'check-in': 'Visitor checked in.',
	'check-out': 'Visitor checked out.',
	'cancel': 'Visit cancelled.',
	'deny': 'Visit denied.',
	'no-show': 'Visit marked as a no-show.',
	'pre-registered': 'Visit marked as pre-registered.',
	'invite': 'Invite sent.'
});

/**
 * One visit lifecycle transition, then a refetch of the current window (and stats) so the grid
 * shows the server's state, not the request's.
 *
 * ⚠️ INVITE CAN HALF-SUCCEED: the transition commits while the email does not go out, and the
 * server says so in `info`/`message`. That nuance is surfaced as a WARNING — reporting a bare
 * success would tell reception to stop chasing a visitor who never received anything.
 */
export function* visitAction({ payload }) {
	const { visitID, action: actionKey, body, reload } = payload || {};
	try {
		if (!VISIT_ACTIONS[actionKey]) throw Error(`Unknown visit action: ${actionKey}`);
		const res = yield call(() => SuperFetch.post(`${BASE}/${visitID}/${actionKey}`, body || {}));
		if (res.statusCode !== 200) throw Error(errMessage(res, 'Action failed.'));
		const serverInfo = res.info || res.message;
		const inviteNotSent = actionKey === 'invite' && res.data?.invite && !res.data.invite.sent;
		Notification(inviteNotSent ? 'warning' : 'success', serverInfo || VISIT_ACTIONS[actionKey]);
		yield put(visitsActions.visitActionSuccess());
	} catch (error) {
		Notification('error', error.message);
		yield put(visitsActions.visitActionError(error));
		return;
	}
	// A failed REFETCH is not a failed action — same doctrine as mutateKiosk: the transition already
	// committed and wrote its event; the list is merely stale.
	//
	// 🛑 TWO windows, not one. `reload.list` is the 3-week grid window; `reload.stats` is the
	// selected DAY — stats buckets are computed relative to `from`, so refetching stats with the
	// list window would silently turn the day tiles into 21-day aggregates after every action.
	//
	// 🛑 `put`, NOT a direct `call` to the generator. `visitAction` is `takeEvery`, so its own
	// refetch is not covered by `loadVisits`'/`loadVisitStats`'s `takeLatest` — a `call` runs a
	// SEPARATE, uncancellable task that can outlive and overwrite a newer read (e.g. the operator
	// flips the day picker right after clicking Check out): the stale action-triggered refetch can
	// resolve after the day-picker's own load and silently leave the wrong day's records on screen.
	// Dispatching instead re-enters the SAME watched channel, so `takeLatest` cancels whichever one
	// is stale, exactly as it does for two ordinary day-picker moves.
	if (reload) {
		if (reload.list) yield put(visitsActions.loadVisits(reload.list));
		if (reload.stats) yield put(visitsActions.loadVisitStats(reload.stats));
	}
}

/**
 * New Visit — schedule a visit, then send its invite in the same operator action.
 *
 * 🛑 `create()` NEVER SENDS AN INVITE (confirmed against `routesV5/visits/classes.js`): a visit
 * created through this route is always `scheduled`, and only the separate `/invite` transition
 * emails the visitor. So "New Visit" is genuinely two server calls — but the second is DISPATCHED
 * (`put(visitsActions.visitAction(...))`), not a raw `yield call` into the `visitAction` generator.
 *
 * 🛑 A raw `call` would run `visitAction`'s body directly, OUTSIDE the normal dispatch channel —
 * which means it still touches the SHARED `actionVisitID` one-shot (via `visitActionSuccess`/
 * `visitActionError`) without ever having gone through `visitAction`'s own reducer to set it first.
 * If an unrelated ROW action happened to be genuinely in flight at that moment, this nested call's
 * completion would clear ITS spinner early. Dispatching instead makes the invite step an ordinary,
 * independently-tracked `visitAction` — exactly as legitimate as two rows acting at once, which the
 * slice already documents as an accepted, cosmetic last-writer-wins case for this same flag.
 *
 * ⚠️ A FAILED INVITE-SEND IS NOT A FAILED CREATE. The visit exists and is genuinely scheduled;
 * `visitAction` already surfaces "transitioned but the email was not sent" as its own warning
 * toast. Reporting the whole New Visit action as failed here would tell the operator to retry
 * create — which would schedule a SECOND visit for the same visitor.
 *
 * 🛑 THE INVITE IS ONLY DISPATCHED WHEN THE VISITOR CARRIES A CHANNEL (see `./helpers.js#hasInviteChannel`
 * — EMAIL ONLY, there is no SMS path). This is NOT the same thing as "an invite that fails to send"
 * above — `classes.js#invite()` transitions the visit to `invited` and WRITES that event BEFORE
 * `sendVisitInvite` even looks at whether there is an email to use, so a no-channel visitor pushed
 * through `/invite` anyway would come back `invited` having had nothing sent, permanently
 * misreporting a `scheduled` visit as contacted. Skipping the dispatch entirely — not just
 * tolerating its silent no-send — is what keeps "New Visit" honest for a walk-in with no email on
 * file (a phone number alone does not count; see the helper's own doc comment for why).
 */
export function* createVisit({ payload }) {
	const { reload, ...body } = payload || {};
	try {
		const res = yield call(() => SuperFetch.post(BASE, body));
		if (res.statusCode !== 201 && res.statusCode !== 200)
			throw Error(errMessage(res, 'Could not create the visit.'));
		const visitID = res.data?.visitID;
		Notification('success', 'Visit created.');
		yield put(visitsActions.createVisitSuccess());
		if (visitID && hasInviteChannel(body.visitor)) {
			yield put(visitsActions.visitAction({ visitID, action: 'invite', reload }));
		} else if (reload) {
			// Nothing to invite — either no visitID (should not happen against the confirmed
			// contract) or no contact channel on the visitor (the expected, common no-invite path).
			// Either way the window is still worth refreshing rather than silently leaving the new
			// visit off the grid.
			if (reload.list) yield put(visitsActions.loadVisits(reload.list));
			if (reload.stats) yield put(visitsActions.loadVisitStats(reload.stats));
		}
	} catch (error) {
		Notification('error', error.message);
		yield put(visitsActions.createVisitError(error));
	}
}

export default function* visitsSaga() {
	yield all([
		// takeLatest for the read: a fast tab-switch must not race two loads into the cache.
		takeLatest(visitsActions.loadTypes.type, loadTypes),
		// takeEvery for writes: two deliberate saves are two saves, and dropping one silently is
		// worse than running both.
		takeEvery(visitsActions.mutateType.type, mutateType),
		// takeEVERY, not takeLatest: several surfaces load DIFFERENT levels concurrently (Devices=site,
		// header QR=site, Settings=org, preview=level). takeLatest would cancel an in-flight level when
		// another dispatches, and the cancelled effect never retries (stable deps) — leaving a surface
		// stuck on inherited defaults. Each level is independent and its success reducer keys by level,
		// so re-fetching the same level is harmlessly idempotent.
		// ⚠️ ACCEPTED residual (low-probability, self-corrects on reload): the global `configLoading`
		// flag and the last-write-wins per-level store are not fully race-safe under two OVERLAPPING
		// SAME-SITE reads (e.g. the always-mounted QR popover fetching site A while site A is saved) —
		// a stale read could momentarily clear a spinner early or overwrite a fresh save. The correct
		// cure is per-level keyed concurrency, deliberately deferred: it is a larger saga change and the
		// window requires two surfaces on the same site at once. Chosen over reverting to takeLatest,
		// whose cross-level cancellation was the worse, more common bug.
		takeEvery(visitsActions.loadSiteConfig.type, loadSiteConfig),
		takeEvery(visitsActions.saveSiteConfig.type, saveSiteConfig),
		takeLatest(visitsActions.loadKiosks.type, loadKiosks),
		takeEvery(visitsActions.mutateKiosk.type, mutateKiosk),
		takeLatest(visitsActions.loadVisits.type, loadVisits),
		// takeLatest: a fast day-flip must not race two stats windows into the one stats slot.
		takeLatest(visitsActions.loadVisitStats.type, loadVisitStats),
		// takeEvery: two deliberate transitions on two rows are two transitions; the server's own
		// preconditions make a double-submit on ONE row benign (the second answers 409/precondition).
		takeEvery(visitsActions.visitAction.type, visitAction),
		// takeEvery, not takeLatest: the New Visit modal's own Create button disables itself while
		// creatingVisit is true, so a double-submit is prevented at the UI, not the saga — takeLatest
		// here would silently cancel a genuine SECOND create (a different visitor) submitted quickly.
		takeEvery(visitsActions.createVisit.type, createVisit)
	]);
}
